import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, ReactNode } from 'react';
import { authService } from '../services/authService';

// User interface with store_id support
interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  phone: string;
  role: string;
  store_id?: number;
  store_ids?: number[];
  hasMultipleStores?: boolean;
  created_at: string;
  permissions?: any;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const response = await authService.getProfile();

          // Transform to match User interface
          const responseUser = response.user as any;
          const userData: User = {
            id: responseUser.id,
            email: responseUser.email,
            first_name: responseUser.first_name || '',
            last_name: responseUser.last_name || '',
            phone: responseUser.phone || '',
            role: responseUser.role,
            store_id: responseUser.store_id,
            store_ids: responseUser.store_ids || (responseUser.store_id ? [responseUser.store_id] : []),
            hasMultipleStores: responseUser.hasMultipleStores || false,
            created_at: responseUser.created_at,
            permissions: response.permissions
          };
          setUser(userData);
        }
      } catch {
        localStorage.removeItem('token');
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    try {
      setLoading(true);
      const response = await authService.login(email, password);
      localStorage.setItem('token', response.token);

      // Transform the response user to match our User interface
      const responseUser = response.user as any;
      const userData: User = {
        id: responseUser.id,
        email: responseUser.email,
        first_name: responseUser.firstName,
        last_name: responseUser.lastName,
        phone: '', // Default empty phone
        role: responseUser.role,
        store_id: responseUser.store_id,
        store_ids: responseUser.store_ids || (responseUser.store_id ? [responseUser.store_id] : []),
        hasMultipleStores: responseUser.hasMultipleStores || false,
        created_at: new Date().toISOString(), // Default timestamp
        permissions: response.permissions, // Include permissions
      };
      setUser(userData);
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setUser(null);
  }, []);

  const updateUser = useCallback((userData: Partial<User>) => {
    setUser((prevUser) => {
      if (prevUser) {
        return { ...prevUser, ...userData };
      }
      return prevUser;
    });
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const value: AuthContextType = useMemo(() => ({
    user,
    loading,
    login,
    logout,
    updateUser,
  }), [user, loading, login, logout, updateUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
